import { Typography, Paper, Box, Button, Grid, Card, CardContent, CardActions } from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { logger } from '../utils/logger';


interface HealthStatus {
  status: string;
  message: string;
}

const HomePage = () => {
  const [healthStatus, setHealthStatus] = useState<HealthStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const checkHealth = async () => {
      try {
        // Use /api/health endpoint (not /health) to match backend
        const data = await apiService.get<HealthStatus>('/api/health');
        setHealthStatus(data);
      } catch (error) {
        logger.error('Failed to fetch health status:', error);
        setHealthStatus(null);
      } finally {
        setLoading(false);
      }
    };

    checkHealth();
  }, []);

  const features = [
    {
      title: 'Dashboard',
      description: 'Thống kê tổng quan hệ thống',
      path: '/dashboard',
      color: '#1976d2'
    },
    {
      title: 'QL sách',
      description: 'Thêm, sửa, xóa sách',
      path: '/books',
      color: '#2e7d32'
    },
    {
      title: 'QL tài nguyên',
      description: 'Upload và ql file',
      path: '/resources',
      color: '#ed6c02'
    },
    {
      title: 'QL giáo án',
      description: 'Tạo và ql lesson plans',
      path: '/lesson-plans',
      color: '#9c27b0'
    },
    {
      title: 'Tìm kiếm',
      description: 'Tìm kiếm sách và tài nguyên',
      path: '/search',
      color: '#d32f2f'
    },
    {
      title: 'Thống kê',
      description: 'Báo cáo và phân tích',
      path: '/reports',
      color: '#5d4037'
    }
  ];

  return (
    <Box sx={{ py: 4 }}>
      <Typography variant="h3" component="h1" gutterBottom align="center">
        Thư Viện Số
      </Typography>
      <Typography variant="h6" component="h2" gutterBottom align="center" color="text.secondary">
        Hệ thống ql thư viện số
      </Typography>

      <Paper sx={{ p: 3, mt: 4 }}>
        <Typography variant="h5" gutterBottom>
          Trạng thái hệ thống
        </Typography>
        {loading ? (
          <Typography>Đang kiểm tra...</Typography>
        ) : healthStatus ? (
          <Box>
            <Typography color="success.main">✅ Trạng thái: {healthStatus.status}</Typography>
            <Typography variant="body2" color="text.secondary">
              {healthStatus.message}
            </Typography>
          </Box>
        ) : (
          <Typography color="error">Không thể kết nối đến backend</Typography>
        )}
      </Paper>

      <Box sx={{ mt: 6 }}>
        <Typography variant="h4" component="h2" gutterBottom align="center">
          Khám phá các tính năng
        </Typography>
        <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 4 }}>
          Chọn một tính năng để bắt đầu sử dụng hệ thống
        </Typography>

        <Grid container spacing={3}>
          {features.map((feature) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={feature.path}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  border: `2px solid ${feature.color}`,
                  '&:hover': {
                    boxShadow: 6,
                    transform: 'translateY(-2px)',
                    transition: 'all 0.3s ease-in-out'
                  }
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Typography
                    variant="h6"
                    component="h3"
                    gutterBottom
                    sx={{ color: feature.color, fontWeight: 'bold' }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {feature.description}
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button
                    size="small"
                    variant="contained"
                    sx={{
                      backgroundColor: feature.color,
                      '&:hover': {
                        backgroundColor: feature.color,
                        opacity: 0.9
                      }
                    }}
                    onClick={() => navigate(feature.path)}
                    fullWidth
                  >
                    Truy cập
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      <Box sx={{ mt: 6, textAlign: 'center' }}>
        <Typography variant="h6" gutterBottom>
          Chưa có tài khoản?
        </Typography>
        <Button
          variant="outlined"
          size="large"
          onClick={() => navigate('/login')}
          sx={{ mt: 2 }}
        >
          Đăng nhập để sử dụng đầy đủ tính năng
        </Button>
      </Box>
    </Box>
  );
};

export default HomePage;
